import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  Badge,
  Tooltip,
  Popover,
  Divider,
  Space,
  Tag,
  Button,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  TagOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import Draggable from "react-draggable";
import dayjs, { Dayjs } from "dayjs";
import "./YearView.css";
import { Event } from "./activityCalendar";
import api from "../../../services/api";
import { message } from "antd";

interface YearViewProps {
  currentDate: Dayjs;
  events: Event[];
  onEventClick: (event: Event) => void;
  onMonthClick: (date: Dayjs) => void;
  onEventSaved?: () => void;
  selectedCompany: { id: string; type: string } | null;
}

const YearView: React.FC<YearViewProps> = ({
  currentDate,
  events,
  onEventClick,
  onMonthClick,
  onEventSaved,
}) => {
  const signature = localStorage.getItem("signature");
  const [isEventDetailVisible, setIsEventDetailVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [detailPosition, setDetailPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [activePopover, setActivePopover] = useState<string | null>(null);

  const year = currentDate.year();
  const months = Array.from({ length: 12 }, (_, i) =>
    dayjs().year(year).month(i).startOf("month")
  );

  const getEventsForMonth = (month: Dayjs): Event[] => {
    return events.filter(
      (event) =>
        event.start.isSame(month, "month") ||
        (event.start.isBefore(month.endOf("month")) && event.end.isAfter(month))
    );
  };

  const getEventsForDay = (month: Dayjs, day: number): Event[] => {
    const date = month.date(day);
    return events.filter(
      (event) =>
        event.start.isSame(date, "day") ||
        (event.start.isBefore(date) && event.end.isAfter(date)) ||
        event.end.isSame(date, "day")
    );
  };

  const getLatestEvent = (monthEvents: Event[]): Event | null => {
    if (monthEvents.length === 0) return null;
    return monthEvents.reduce((latest, event) =>
      event.start.isAfter(latest.start) ? event : latest
    );
  };

  // Handle event click - show detail card instead of modal
  const handleEventClick = (event: Event, clickEvent: React.MouseEvent) => {
    setSelectedEvent(event);
    setActivePopover(null); // Close any open popover

    const container = document.querySelector(".year-view") as HTMLElement;
    const containerRect = container.getBoundingClientRect();

    let x = clickEvent.clientX - containerRect.left;
    let y = clickEvent.clientY - containerRect.top;

    const cardWidth = 400;
    const cardHeight = 500;

    if (x + cardWidth > containerRect.width) {
      x = containerRect.width - cardWidth - 10;
    }
    if (y + cardHeight > containerRect.height) {
      y = containerRect.height - cardHeight - 10;
    }
    if (x < 0) x = 10;
    if (y < 0) y = 10;

    setDetailPosition({ x, y });
    setIsEventDetailVisible(true);
  };

  // Handle edit event - open the modal through parent
  const handleEditEvent = () => {
    if (selectedEvent) {
      onEventClick(selectedEvent);
      setIsEventDetailVisible(false);
      setSelectedEvent(null);
    }
  };

  // Handle delete event
  const handleDeleteEvent = async () => {
    if (!selectedEvent?.id) return;

    try {
      await api.post("activityCalendarService/deleteEvent", {
        signature,
        id: selectedEvent.id,
      });
      message.success("Event deleted successfully");
      setIsEventDetailVisible(false);
      setSelectedEvent(null);
      onEventSaved?.();
    } catch (err) {
      message.error("Failed to delete event");
    }
  };

  const handleCloseDetail = () => {
    setIsEventDetailVisible(false);
    setSelectedEvent(null);
  };

  // Handle popover visibility change
  const handlePopoverVisibleChange = (visible: boolean, popoverId: string) => {
    if (visible) {
      setActivePopover(popoverId);
    } else {
      setActivePopover(null);
    }
  };

  const formatEventTime = (event: Event): string => {
    if (event.allDay) return "All day";
    return `${event.start.format("h:mm A")} - ${event.end.format("h:mm A")}`;
  };

  const renderMonthCalendar = (month: Dayjs) => {
    const monthName = month.format("MMM");
    const daysInMonth = month.daysInMonth();
    const firstDay = month.startOf("month").day(); // 0 = Sunday, 1 = Monday, etc.

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="year-day empty-day"></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayEvents = getEventsForDay(month, day);
      const isToday = month.date(day).isSame(dayjs(), "day");
      const hasEvents = dayEvents.length > 0;

      days.push(
        <div
          key={`day-${day}`}
          className={`year-day ${isToday ? "today" : ""}`}
        >
          <div className={`year-day-number ${isToday ? "today" : ""}`}>
            {day}
          </div>
          <div className="year-day-events">
            {hasEvents && (
              <Tooltip
                title={
                  dayEvents.length === 1
                    ? `${dayEvents[0].title} (${dayEvents[0].start.format(
                        "HH:mm"
                      )})`
                    : `${dayEvents.length} events on this day`
                }
              >
                <div
                  className="year-event-indicator"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (dayEvents.length === 1) {
                      handleEventClick(dayEvents[0], e);
                    }
                    // For multiple events, the tooltip shows the count
                  }}
                >
                  •
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Popover content for remaining events in a month
  const renderRemainingEventsPopover = (monthEvents: Event[]) => {
    const remainingEvents = monthEvents.slice(1); // Skip first (latest) event

    return (
      <div className="remaining-events-popover">
        <div className="popover-title">
          All Events in {monthEvents[0]?.start.format("MMM YYYY")}
        </div>
        <div className="popover-events-list">
          {remainingEvents.map((event) => (
            <div
              key={event.id}
              className="popover-event-item"
              onClick={(e) => {
                e.stopPropagation();
                handleEventClick(event, e as any);
              }}
            >
              <div
                className="popover-event-color"
                style={{ backgroundColor: event.color }}
              />
              <div className="popover-event-content">
                <div className="popover-event-title">{event.title}</div>
                <div className="popover-event-time">
                  {formatEventTime(event)}
                </div>
                {event.description && (
                  <div className="popover-event-description">
                    {event.description}
                  </div>
                )}
                {event.is_public && (
                  <div className="popover-event-public">🌐 Public Event</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="year-view" style={{ position: "relative" }}>
      <Row gutter={[16, 16]}>
        {months.map((month, index) => {
          const monthEvents = getEventsForMonth(month);
          const isCurrentMonth = month.isSame(dayjs(), "month");
          const latestEvent = getLatestEvent(monthEvents);
          const remainingEventsCount = monthEvents.length - 1;
          const popoverId = `month-${month.format("YYYY-MM")}`;

          return (
            <Col key={month.format("YYYY-MM")} xs={24} sm={12} md={8} lg={6}>
              <Card
                className={`year-month-card ${
                  isCurrentMonth ? "current-month" : ""
                }`}
                onClick={() => onMonthClick(month)}
                hoverable
              >
                <div className="year-month-header">
                  <h4 className="year-month-name">{monthNames[index]}</h4>
                  {monthEvents.length > 0 && (
                    <Badge
                      count={monthEvents.length}
                      style={{ backgroundColor: "#1890ff" }}
                    />
                  )}
                </div>

                <div className="year-month-days">
                  {/* Day headers */}
                  <div className="year-days-header">
                    {["S", "M", "T", "W", "T", "F", "S"].map((day) => (
                      <div key={day} className="year-day-header">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar grid */}
                  <div className="year-month-grid">
                    {renderMonthCalendar(month)}
                  </div>
                </div>

                {/* Month summary */}
                {monthEvents.length > 0 && (
                  <div className="year-month-summary">
                    <div className="year-month-events">
                      {latestEvent && (
                        <Tooltip key={latestEvent.id} title={latestEvent.title}>
                          <div
                            className="year-month-event"
                            style={{ borderLeftColor: latestEvent.color }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEventClick(latestEvent, e);
                            }}
                          >
                            {latestEvent.title}
                          </div>
                        </Tooltip>
                      )}
                      {remainingEventsCount > 0 && (
                        <Popover
                          content={renderRemainingEventsPopover(monthEvents)}
                          trigger="click"
                          placement="bottom"
                          overlayClassName="remaining-events-popover-overlay"
                          open={activePopover === popoverId}
                          onOpenChange={(visible) =>
                            handlePopoverVisibleChange(visible, popoverId)
                          }
                        >
                          <div
                            className="year-month-more"
                            onClick={(e) => e.stopPropagation()}
                          >
                            +{remainingEventsCount} more events
                          </div>
                        </Popover>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Event Detail Card */}
      {isEventDetailVisible && selectedEvent && detailPosition && (
        <Draggable
          handle=".card-header"
          bounds=".year-view"
          defaultPosition={detailPosition}
        >
          <Card
            className="event-detail-card"
            style={{
              width: 400,
              zIndex: 1001,
              position: "absolute",
              top: 0,
              left: 0,
            }}
            styles={{
              body: { padding: 0 },
            }}
          >
            {/* Custom Header */}
            <div
              className="card-header detail-header"
              style={{
                background: `linear-gradient(135deg, ${selectedEvent.color}20 0%, ${selectedEvent.color}40 100%)`,
                borderLeft: `4px solid ${selectedEvent.color}`,
              }}
            >
              <div className="card-header-content">
                <div className="card-title-section">
                  <div
                    className="card-icon"
                    style={{ backgroundColor: selectedEvent.color }}
                  >
                    <CalendarOutlined
                      style={{ color: "white", fontSize: "16px" }}
                    />
                  </div>
                  <div>
                    <div className="card-title">{selectedEvent.title}</div>
                    <div className="card-subtitle">
                      {formatEventTime(selectedEvent)}
                    </div>
                  </div>
                </div>
                <Button
                  type="text"
                  onClick={handleCloseDetail}
                  className="close-btn"
                  icon={<CloseOutlined />}
                />
              </div>
            </div>

            {/* Detail Content */}
            <div className="card-body">
              <div className="event-details">
                {/* Event Information */}
                <div className="detail-section">
                  <div className="detail-item">
                    <div className="detail-label">
                      <ClockCircleOutlined className="detail-icon" />
                      Time
                    </div>
                    <div className="detail-value">
                      {selectedEvent.allDay ? (
                        "All Day"
                      ) : (
                        <>
                          {selectedEvent.start.format(
                            "ddd, MMM D, YYYY h:mm A"
                          )}
                          <br />
                          to{" "}
                          {selectedEvent.end.format("ddd, MMM D, YYYY h:mm A")}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-label">
                      <TagOutlined className="detail-icon" />
                      Category
                    </div>
                    <div className="detail-value">
                      <Tag
                        color={selectedEvent.color}
                        style={{ border: `1px solid ${selectedEvent.color}` }}
                      >
                        {selectedEvent.category}
                      </Tag>
                    </div>
                  </div>

                  {selectedEvent.location && (
                    <div className="detail-item">
                      <div className="detail-label">
                        <EnvironmentOutlined className="detail-icon" />
                        Location
                      </div>
                      <div className="detail-value">
                        {selectedEvent.location}
                      </div>
                    </div>
                  )}

                  <div className="detail-item">
                    <div className="detail-label">
                      <FileTextOutlined className="detail-icon" />
                      Visibility
                    </div>
                    <div className="detail-value">
                      <Tag color={selectedEvent.is_public ? "blue" : "default"}>
                        {selectedEvent.is_public
                          ? "Public Event"
                          : "Private Event"}
                      </Tag>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {selectedEvent.description && (
                  <>
                    <Divider style={{ margin: "16px 0" }} />
                    <div className="detail-section">
                      <div className="detail-item">
                        <div className="detail-label">
                          <FileTextOutlined className="detail-icon" />
                          Description
                        </div>
                        <div className="detail-value description-text">
                          {selectedEvent.description}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Action Buttons */}
                <Divider style={{ margin: "16px 0" }} />
                <div className="detail-actions">
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <Button
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteEvent}
                      size="middle"
                      disabled={selectedEvent?.is_generated}
                    >
                      Delete
                    </Button>
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={handleEditEvent}
                      size="middle"
                      disabled={selectedEvent?.is_generated}
                    >
                      Edit Event
                    </Button>
                  </Space>
                </div>
              </div>
            </div>
          </Card>
        </Draggable>
      )}
    </div>
  );
};

export default YearView;
